import { z } from 'zod';
import { fredSeriesSyncStatusSchema, fredSeasonalAdjustmentSchema, fredFrequencySchema, fredScaleSchema, fredSeriesGroupSchema, dateTimeSchema, fredSyncScopeSchema, fredSyncTriggerSchema, fredSyncRunStatusSchema, usStateOrTerritorySchema, fredMetricFamilySchema } from '../../enums.mjs';
import { fredSeriesCode, fredSeriesId, fredSyncRunId } from '../../types/opaque.mjs';

const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected an ISO date, YYYY-MM-DD");
var Series;
(function (Series) {
    Series.responseSchema = z.object({
        id: fredSeriesId,
        seriesCode: fredSeriesCode,
        label: z.string(),
        description: z.string(),
        seriesGroup: fredSeriesGroupSchema,
        seriesGroupLabel: z.string(),
        scale: fredScaleSchema,
        scaleLabel: z.string(),
        frequency: fredFrequencySchema,
        frequencyLabel: z.string(),
        seasonalAdjustment: fredSeasonalAdjustmentSchema,
        seasonalAdjustmentLabel: z.string(),
        isDiscontinued: z.boolean(),
        isSuretyFocus: z.boolean(),
        sortOrder: z.number().int(),
        fredTitle: z.string().nullable(),
        fredUnits: z.string().nullable(),
        fredLastUpdated: dateTimeSchema.nullable(),
        observationStart: dateTimeSchema.nullable(),
        observationEnd: dateTimeSchema.nullable(),
        syncStatus: fredSeriesSyncStatusSchema,
        syncError: z.string().nullable(),
        lastSyncedAt: dateTimeSchema.nullable(),
        observationCount: z.number().int().nullable(),
    });
})(Series || (Series = {}));
var Observation;
(function (Observation) {
    Observation.responseSchema = z.object({
        date: isoDateSchema,
        value: z.number(),
    });
})(Observation || (Observation = {}));
var ListSeries;
(function (ListSeries) {
    ListSeries.responseSchema = z.array(Series.responseSchema);
})(ListSeries || (ListSeries = {}));
var GetSeries;
(function (GetSeries) {
    (function (Request) {
        Request.schema = z.object({
            from: isoDateSchema.optional(),
            to: isoDateSchema.optional(),
        });
    })(GetSeries.Request || (GetSeries.Request = {}));
    GetSeries.responseSchema = z.object({
        series: Series.responseSchema,
        observations: z.array(Observation.responseSchema),
        observationAgeDays: z.number().int().nullable(),
    });
})(GetSeries || (GetSeries = {}));
const seriesFailureSchema = z.object({
    seriesCode: z.string(),
    message: z.string(),
});
var SyncRun;
(function (SyncRun) {
    SyncRun.responseSchema = z.object({
        id: fredSyncRunId,
        status: fredSyncRunStatusSchema,
        trigger: fredSyncTriggerSchema,
        scope: fredSyncScopeSchema,
        startedAt: dateTimeSchema,
        completedAt: dateTimeSchema.nullable(),
        heartbeatAt: dateTimeSchema.nullable(),
        isStale: z.boolean(),
        seriesAttempted: z.number().int(),
        seriesSucceeded: z.number().int(),
        seriesSkipped: z.number().int(),
        seriesUnavailable: z.number().int(),
        seriesFailed: z.number().int(),
        observationsUpserted: z.number().int(),
        errorMessage: z.string().nullable(),
        failures: z.array(seriesFailureSchema),
    });
})(SyncRun || (SyncRun = {}));
var GetLatestSyncRun;
(function (GetLatestSyncRun) {
    GetLatestSyncRun.responseSchema = SyncRun.responseSchema.nullable();
})(GetLatestSyncRun || (GetLatestSyncRun = {}));
var GetSyncRun;
(function (GetSyncRun) {
    GetSyncRun.responseSchema = SyncRun.responseSchema;
})(GetSyncRun || (GetSyncRun = {}));
var Snapshot;
(function (Snapshot) {
    Snapshot.changePeriodSchema = z.enum(["mom", "qoq", "yoy"]);
    (function (Request) {
        Request.schema = z.object({
            stateCode: usStateOrTerritorySchema.optional(),
            changePeriod: Snapshot.changePeriodSchema.optional(),
        });
    })(Snapshot.Request || (Snapshot.Request = {}));
    Snapshot.pointSchema = z.object({
        date: isoDateSchema,
        value: z.number(),
    });
    Snapshot.cardSchema = z.object({
        seriesCode: fredSeriesCode,
        label: z.string(),
        description: z.string(),
        scale: fredScaleSchema,
        scaleLabel: z.string(),
        frequency: fredFrequencySchema,
        seasonalAdjustmentLabel: z.string(),
        frequencyLabel: z.string(),
        isDiscontinued: z.boolean(),
        stateCode: usStateOrTerritorySchema.nullable(),
        metricFamily: fredMetricFamilySchema.nullable(),
        display: z.string(),
        displayFull: z.string(),
        latestValue: z.number().nullable(),
        latestDate: isoDateSchema.nullable(),
        priorValue: z.number().nullable(),
        priorDate: isoDateSchema.nullable(),
        changeDisplay: z.string().nullable(),
        changeDirection: z.enum(["up", "down"]).nullable(),
        sparkline: z.array(Snapshot.pointSchema),
        observationAgeDays: z.number().int().nullable(),
    });
    Snapshot.groupSchema = z.object({
        seriesGroup: fredSeriesGroupSchema,
        groupLabel: z.string(),
        cards: z.array(Snapshot.cardSchema),
    });
    Snapshot.stateGroupSchema = z.object({
        stateCode: usStateOrTerritorySchema,
        cards: z.array(Snapshot.cardSchema),
    });
    Snapshot.responseSchema = z.object({
        groups: z.array(Snapshot.groupSchema),
        stateGroup: Snapshot.stateGroupSchema.nullable(),
        availableStateCodes: z.array(usStateOrTerritorySchema),
        stateCode: usStateOrTerritorySchema.nullable(),
        changePeriod: Snapshot.changePeriodSchema,
        asOf: isoDateSchema.nullable(),
    });
})(Snapshot || (Snapshot = {}));
var Refresh;
(function (Refresh) {
    (function (Request) {
        Request.schema = z.object({
            scope: fredSyncScopeSchema.optional(),
        });
    })(Refresh.Request || (Refresh.Request = {}));
    Refresh.acceptedSchema = z.object({
        runId: fredSyncRunId,
        status: fredSyncRunStatusSchema,
        scope: fredSyncScopeSchema,
    });
})(Refresh || (Refresh = {}));

export { GetLatestSyncRun, GetSeries, GetSyncRun, ListSeries, Observation, Refresh, Series, Snapshot, SyncRun, seriesFailureSchema };
